import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Badge,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaRegEdit,
  FaPlus,
  FaTrashAlt,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaTags,
} from "react-icons/fa";
import Message from "../components/Message";
import Loader from "../components/Loader";
import {
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} from "../slices/customerSlice";
import { useGetOrdersQuery } from "../slices/OrdersSlice";
import { useGetMyOutletFoodItemsQuery } from "../slices/FoodItemSilce";
import useCustomersCache from "../useCustomersCache";
import { customerMobileOf, formatAmount } from "../utils/commonUtils";

// Default selling price of a food item: first size's itemPrice minus
// its percent and flat discounts
const defaultSellingPrice = (foodItem) => {
  const size = foodItem?.availableSizes?.[0];
  if (!size) return 0;
  const itemPrice = Number(size.itemPrice) || 0;
  const discountPercent =
    Number(size.discountPercent) > 0 ? Number(size.discountPercent) : 0;
  const discountAmount =
    Number(size.discountAmount) > 0 ? Number(size.discountAmount) : 0;
  return Number(
    (itemPrice - (itemPrice * discountPercent) / 100 - discountAmount).toFixed(
      2
    )
  );
};

const emptyCustomerForm = {
  customerMobile: "",
  visitorId: "",
  customerName: "",
  customerAddress: "",
  customerPincode: "",
  customerState: "",
  customerGst: "",
  customerEmails: [""],
  secondaryMobiles: [""],
  postPaidCustomer: false,
};

const CustomersScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const { customersList, isLoading, error, refetch } = useCustomersCache();

  const navigate = useNavigate();

  // All orders, aggregated per mobile number (customerMobileOf(customer)): total due, total paid (grossTotal - dueAmount) and the time
  // of the customer's latest order - used for the badges and sorting
  const { data: allOrders } = useGetOrdersQuery({
    query: "sort=-createdAt",
    token: userInfo?.token,
  });

  const statsByMobile = useMemo(() => {
    const stats = {};
    (Array.isArray(allOrders) ? allOrders : []).forEach((order) => {
      const mobile = String(order?.mobileNumber || "").trim();
      if (!mobile) return;
      if (!stats[mobile]) {
        stats[mobile] = {
          due: 0,
          paid: 0,
          gross: 0,
          count: 0,
          lastOrderTime: 0,
          orders: [],
        };
      }
      const gross = Number(order?.grossTotal || 0);
      const due = Number(order?.dueAmount || 0);
      stats[mobile].due += due;
      stats[mobile].paid += Math.max(gross - due, 0);
      stats[mobile].gross += gross;
      stats[mobile].count += 1;
      // allOrders is fetched sorted by -createdAt, so pushing keeps the
      // customer's orders newest first
      stats[mobile].orders.push(order);
      const orderTime = new Date(order?.createdAt || 0).getTime();
      if (orderTime > stats[mobile].lastOrderTime) {
        stats[mobile].lastOrderTime = orderTime;
      }
    });
    return stats;
  }, [allOrders]);

  const [createCustomer, { isLoading: creating }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: updating }] = useUpdateCustomerMutation();

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyCustomerForm);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [expandedCustomers, setExpandedCustomers] = useState({});
  // Mobile number of the customer whose orders list is open below their row
  const [ordersOpenFor, setOrdersOpenFor] = useState(null);

  // Customer whose per-product prices are being edited, and the draft
  // price values keyed by food item id
  const [priceCustomer, setPriceCustomer] = useState(null);
  const [priceDrafts, setPriceDrafts] = useState({});
  const [priceSearch, setPriceSearch] = useState("");

  // Food items are only fetched once the Product Prices modal is opened
  const { data: foodItems, isLoading: foodItemsLoading } =
    useGetMyOutletFoodItemsQuery(
      { token: userInfo?.token },
      { skip: !priceCustomer }
    );

  // Prefill drafts: customised price if the customer has one for the
  // item, otherwise the item's default selling price
  useEffect(() => {
    if (!priceCustomer || !foodItems?.length) return;
    const drafts = {};
    foodItems.forEach((foodItem) => {
      const id = String(foodItem._id);
      const custom = priceCustomer.customisedSellingPrice?.[id]?.sellingPrice;
      drafts[id] =
        custom !== undefined && custom !== null
          ? formatAmount(custom)
          : formatAmount(defaultSellingPrice(foodItem));
    });
    setPriceDrafts(drafts);
  }, [priceCustomer, foodItems]);

  const handleClosePrices = () => {
    setPriceCustomer(null);
    setPriceDrafts({});
    setPriceSearch("");
  };

  const handleSubmitPrices = async () => {
    const invalid = Object.entries(priceDrafts).find(
      ([, value]) =>
        String(value).trim() !== "" &&
        (!Number.isFinite(Number(value)) || Number(value) < 0)
    );
    if (invalid) {
      toast.error("Please provide valid prices (0 or above)");
      return;
    }

    // Send only real customisations: previously customised items (kept
    // or edited) and items whose draft differs from the default price.
    // Items left at their default (or blank) are not customised
    const customised = {};
    (foodItems || []).forEach((foodItem) => {
      const id = String(foodItem._id);
      const draft = String(priceDrafts[id] ?? "").trim();
      if (draft === "") return;
      const value = Number(draft);
      const wasCustomised =
        priceCustomer?.customisedSellingPrice?.[id] !== undefined;
      if (
        wasCustomised ||
        Math.abs(value - defaultSellingPrice(foodItem)) > 0.001
      ) {
        customised[id] = { sellingPrice: value };
      }
    });

    try {
      await updateCustomer({
        token: userInfo?.token,
        payload: {
          customerMobile: Number(priceCustomer.mobileNumber),
          customisedSellingPrice: customised,
        },
      }).unwrap();
      toast.success("Product prices updated successfully");
      handleClosePrices();
      refetch();
    } catch (err) {
      console.log(err);
      toast.error(
        err?.data?.message ||
          err?.data?.error ||
          "Failed to update product prices"
      );
    }
  };

  const toggleShowMore = (customerId) => {
    setExpandedCustomers((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  const filteredCustomers = useMemo(() => {
    const list = customersList.filter((customer) => {
      if (!searchText) return true;
      const search = searchText.toLowerCase();
      return (
        customer?.customerName?.toLowerCase().includes(search) ||
        customerMobileOf(customer).includes(search) ||
        customer?.customerState?.toLowerCase().includes(search)
      );
    });

    const statOf = (customer, key) =>
      statsByMobile[customerMobileOf(customer)]?.[key] || 0;

    if (sortBy === "due") {
      list.sort((a, b) => statOf(b, "due") - statOf(a, "due"));
    } else if (sortBy === "paid") {
      list.sort((a, b) => statOf(b, "paid") - statOf(a, "paid"));
    } else if (sortBy === "gross") {
      list.sort((a, b) => statOf(b, "gross") - statOf(a, "gross"));
    } else if (sortBy === "orders") {
      list.sort((a, b) => statOf(b, "count") - statOf(a, "count"));
    } else if (sortBy === "latest") {
      list.sort(
        (a, b) => statOf(b, "lastOrderTime") - statOf(a, "lastOrderTime")
      );
    }
    return list;
  }, [customersList, searchText, sortBy, statsByMobile]);

  const handleOpenCreate = () => {
    setIsEditMode(false);
    setFormData(emptyCustomerForm);
    setShowModal(true);
  };

  const handleOpenEdit = (customer) => {
    setIsEditMode(true);
    setFormData({
      customerMobile: customerMobileOf(customer),
      visitorId: customer.visitorId || "",
      customerName: customer.customerName || "",
      customerAddress: customer.customerAddress || "",
      customerPincode: customer.customerPincode || "",
      customerState: customer.customerState || "",
      customerGst: customer.customerGst || "",
      customerEmails: customer.customerEmails?.length
        ? [...customer.customerEmails]
        : [""],
      secondaryMobiles: customer.secondaryMobiles?.length
        ? customer.secondaryMobiles.map(String)
        : [""],
      postPaidCustomer: Boolean(customer.postPaidCustomer),
    });
    setShowModal(true);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleListChange = (field, index, value) => {
    setFormData((prev) => {
      const updatedList = [...prev[field]];
      updatedList[index] = value;
      return { ...prev, [field]: updatedList };
    });
  };

  const handleAddListItem = (field) => {
    setFormData((prev) => ({ ...prev, [field]: [...prev[field], ""] }));
  };

  const handleRemoveListItem = (field, index) => {
    setFormData((prev) => {
      const updatedList = prev[field].filter((item, i) => i !== index);
      return { ...prev, [field]: updatedList.length ? updatedList : [""] };
    });
  };

  const verifyInputFields = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!String(formData.customerMobile).match(/^\d{10}$/)) {
      toast.error("Primary Mobile Number should be a valid 10 digit number");
      return false;
    }
    if (!formData.customerName?.trim()) {
      toast.error("Customer Name is required");
      return false;
    }
    if (
      formData.customerPincode &&
      !String(formData.customerPincode).match(/^\d{6}$/)
    ) {
      toast.error("Pincode should be a valid 6 digit number");
      return false;
    }
    const invalidEmail = formData.customerEmails.find(
      (email) => email.trim() && !emailRegex.test(email.trim())
    );
    if (invalidEmail) {
      toast.error(`"${invalidEmail}" is not a valid email`);
      return false;
    }
    const invalidMobile = formData.secondaryMobiles.find(
      (mobile) => String(mobile).trim() && !String(mobile).match(/^\d{10,12}$/)
    );
    if (invalidMobile) {
      toast.error(`"${invalidMobile}" is not a valid mobile number`);
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    customerMobile: Number(formData.customerMobile),
    visitorId: formData.visitorId,
    customerName: formData.customerName.trim(),
    customerAddress: formData.customerAddress,
    customerPincode: formData.customerPincode
      ? Number(formData.customerPincode)
      : undefined,
    customerState: formData.customerState,
    customerGst: formData.customerGst,
    customerEmails: formData.customerEmails
      .map((email) => email.trim())
      .filter((email) => email),
    secondaryMobiles: formData.secondaryMobiles
      .map((mobile) => String(mobile).trim())
      .filter((mobile) => mobile)
      .map(Number),
    postPaidCustomer: formData.postPaidCustomer,
  });

  const handleSubmit = async () => {
    if (!verifyInputFields()) return;

    try {
      if (isEditMode) {
        await updateCustomer({
          token: userInfo?.token,
          payload: buildPayload(),
        }).unwrap();
        toast.success("Customer Updated Successfully");
      } else {
        await createCustomer({
          token: userInfo?.token,
          payload: buildPayload(),
        }).unwrap();
        toast.success("Customer Created Successfully");
      }
      setShowModal(false);
      setFormData(emptyCustomerForm);
      refetch();
    } catch (err) {
      console.log(err);
      toast.error(
        err?.data?.message ||
          err?.data?.error ||
          `Failed to ${isEditMode ? "update" : "create"} customer`
      );
    }
  };

  return (
    <>
      <Row
        className="align-items-center mb-3 mx-1 mt-2"
        style={{ rowGap: "0.5rem" }}
      >
        <Col xs={12} md={3}>
          <h5 className="mb-0">
            <strong>Customers</strong>{" "}
            {!isLoading && !error && (
              <Badge bg="secondary" pill>
                {filteredCustomers.length}
              </Badge>
            )}
          </h5>
        </Col>
        <Col xs={12} md={4}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, mobile or state..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs={12} md={2}>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            title="Sort customers"
          >
            <option value="default">Sort : Default</option>
            <option value="due">Sort : Due Amount</option>
            <option value="paid">Sort : Paid Amount</option>
            <option value="gross">Sort : Gross Total</option>
            <option value="orders">Sort : Total Orders</option>
            <option value="latest">Sort : Latest Order</option>
          </Form.Select>
        </Col>
        <Col xs={12} md={3} className="text-md-end">
          <Button
            className={`${userInfo?.themeColor}ThemeHeaderTop`}
            onClick={handleOpenCreate}
          >
            <FaPlus style={{ marginRight: "5px", marginBottom: "2px" }} />
            Add New Customer
          </Button>
        </Col>
      </Row>

      {isLoading && customersList.length === 0 ? (
        <Loader />
      ) : error && customersList.length === 0 ? (
        <Message>{error?.data?.error || "Error fetching Customers"}</Message>
      ) : filteredCustomers.length === 0 ? (
        <Message>No customers found</Message>
      ) : (
        <Row className="mx-1 mb-5" style={{ rowGap: "0.8rem" }}>
          {filteredCustomers.map((customer) => (
            <React.Fragment key={customer._id}>
              <Col xs={12} sm={6} lg={4}>
                <Card
                  className="h-100 shadow-sm"
                  style={{ borderRadius: "12px" }}
                >
                  <Card.Body style={{ padding: "0.9rem" }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <Card.Title as="h6" className="mb-1">
                        <strong>{customer.customerName}</strong>{" "}
                        {statsByMobile[customerMobileOf(customer)]?.count > 0 && (
                          <span
                            title="Click to see all orders of this customer"
                            style={{
                              fontSize: "0.78rem",
                              fontWeight: "600",
                              color: "#0d6efd",
                              textDecoration: "underline",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                            onClick={() =>
                              setOrdersOpenFor(
                                ordersOpenFor === String(customer._id)
                                  ? null
                                  : String(customer._id)
                              )
                            }
                          >
                            {statsByMobile[customerMobileOf(customer)].count} Orders
                          </span>
                        )}{" "}
                        {statsByMobile[customerMobileOf(customer)]?.gross > 0 && (
                          <Badge
                            bg="info"
                            pill
                            title="Total gross amount across this customer's orders"
                            style={{ fontSize: "0.68rem" }}
                          >
                            Gross ₹
                            {formatAmount(
                              statsByMobile[customerMobileOf(customer)].gross
                            )}
                          </Badge>
                        )}{" "}
                        {statsByMobile[customerMobileOf(customer)]?.paid > 0 && (
                          <Badge
                            bg="success"
                            pill
                            title="Total amount paid across this customer's orders"
                            style={{ fontSize: "0.68rem" }}
                          >
                            Paid ₹
                            {formatAmount(
                              statsByMobile[customerMobileOf(customer)].paid
                            )}
                          </Badge>
                        )}{" "}
                        {statsByMobile[customerMobileOf(customer)]?.due > 0 && (
                          <Badge
                            bg="danger"
                            pill
                            title="Total pending amount across this customer's orders"
                            style={{ fontSize: "0.68rem" }}
                          >
                            Due ₹
                            {formatAmount(
                              statsByMobile[customerMobileOf(customer)].due
                            )}
                          </Badge>
                        )}
                      </Card.Title>
                      <Badge
                        bg={customer.postPaidCustomer ? "success" : "secondary"}
                        pill
                      >
                        {customer.postPaidCustomer ? "Postpaid" : "Prepaid"}
                      </Badge>
                    </div>

                    <div className="mb-1">
                      <FaPhoneAlt
                        size={12}
                        style={{ marginRight: "6px", color: "grey" }}
                      />
                      {customerMobileOf(customer)}
                    </div>
                    {customer.customerAddress && (
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.85rem" }}
                        title={customer.customerAddress}
                      >
                        <FaMapMarkerAlt
                          size={12}
                          style={{ marginRight: "6px", color: "grey" }}
                        />
                        {customer.customerAddress}
                      </div>
                    )}

                    {expandedCustomers[customer._id] && (
                      <div
                        className="mt-2 pt-2"
                        style={{
                          borderTop: "1px dashed #ccc",
                          fontSize: "0.85rem",
                        }}
                      >
                        <Row style={{ rowGap: "0.3rem" }}>
                          <Col xs={6}>
                            <span className="text-muted">State:</span>
                            <br />
                            {customer.customerState || "-"}
                          </Col>
                          <Col xs={6}>
                            <span className="text-muted">Pincode:</span>
                            <br />
                            {customer.customerPincode || "-"}
                          </Col>
                          <Col xs={12}>
                            <span className="text-muted">GST:</span>
                            <br />
                            {customer.customerGst || "-"}
                          </Col>
                          <Col xs={12}>
                            <span className="text-muted">
                              <FaEnvelope
                                size={11}
                                style={{ marginRight: "4px" }}
                              />
                              Emails:
                            </span>
                            <br />
                            {customer.customerEmails?.length
                              ? customer.customerEmails.map((email) => (
                                  <div key={email}>{email}</div>
                                ))
                              : "-"}
                          </Col>
                          <Col xs={12}>
                            <span className="text-muted">
                              <FaPhoneAlt
                                size={11}
                                style={{ marginRight: "4px" }}
                              />
                              Secondary Mobiles:
                            </span>
                            <br />
                            {customer.secondaryMobiles?.length
                              ? customer.secondaryMobiles.map((mobile) => (
                                  <div key={mobile}>{mobile}</div>
                                ))
                              : "-"}
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Card.Body>

                  <Card.Footer
                    className="d-flex justify-content-between bg-white"
                    style={{
                      padding: "0.5rem 0.9rem",
                      borderTop: "1px solid #eee",
                      borderRadius: "0 0 12px 12px",
                    }}
                  >
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={() => toggleShowMore(customer._id)}
                    >
                      {expandedCustomers[customer._id] ? (
                        <>
                          Show Less{" "}
                          <FaChevronUp
                            size={11}
                            style={{ marginBottom: "2px" }}
                          />
                        </>
                      ) : (
                        <>
                          Show More{" "}
                          <FaChevronDown
                            size={11}
                            style={{ marginBottom: "2px" }}
                          />
                        </>
                      )}
                    </Button>
                    <div className="d-flex" style={{ gap: "0.3rem" }}>
                      <Button
                        style={{ border: "none" }}
                        variant="outline-success"
                        size="sm"
                        onClick={() => setPriceCustomer(customer)}
                      >
                        <FaTags style={{ marginBottom: "2px" }} /> Product
                        Prices
                      </Button>
                      <Button
                        style={{ border: "none" }}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleOpenEdit(customer)}
                      >
                        <FaRegEdit style={{ marginBottom: "2px" }} /> Edit
                      </Button>
                    </div>
                  </Card.Footer>
                </Card>
              </Col>

              {/* Orders list for this customer - full width, below their row */}
              {ordersOpenFor === String(customer._id) && (
                <Col xs={12}>
                  <Card className="shadow-sm" style={{ borderRadius: "12px" }}>
                    <Card.Header
                      className="d-flex justify-content-between align-items-center bg-white"
                      style={{
                        borderBottom: "1px solid #eee",
                        borderRadius: "12px 12px 0 0",
                        padding: "0.6rem 0.9rem",
                      }}
                    >
                      <strong style={{ fontSize: "0.92rem" }}>
                        Orders of {customer.customerName} (
                        {statsByMobile[customerMobileOf(customer)]?.count || 0})
                      </strong>
                      <Button
                        variant="secondary"
                        size="sm"
                        style={{ borderRadius: "8px" }}
                        onClick={() => setOrdersOpenFor(null)}
                      >
                        ✕ Close
                      </Button>
                    </Card.Header>
                    <div style={{ maxHeight: "50vh", overflow: "auto" }}>
                      <Table hover className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th style={{ whiteSpace: "nowrap" }}>Order</th>
                            <th style={{ whiteSpace: "nowrap" }}>Date</th>
                            <th>Items</th>
                            <th style={{ whiteSpace: "nowrap" }}>Status</th>
                            <th
                              style={{
                                whiteSpace: "nowrap",
                                textAlign: "right",
                              }}
                            >
                              Amount
                            </th>
                            <th
                              style={{
                                whiteSpace: "nowrap",
                                textAlign: "right",
                              }}
                            >
                              Due
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(
                            statsByMobile[customerMobileOf(customer)]?.orders || []
                          ).map((order) => (
                            <tr key={order._id}>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <span
                                  style={{
                                    color: "#1d5f91",
                                    fontWeight: "600",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    navigate("/orderDetails", {
                                      state: { orderResponse: order },
                                    })
                                  }
                                >
                                  {order.orderId}
                                </span>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {order.createdAt
                                  ? new Date(order.createdAt)
                                      .toISOString()
                                      .slice(0, 10)
                                  : "-"}
                              </td>
                              <td style={{ fontSize: "0.85rem" }}>
                                {(order.orderDetails || [])
                                  .map(
                                    (item) =>
                                      `${item.itemName} × ${item.itemQuantity}`
                                  )
                                  .join(", ")}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {order.orderStatus}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  textAlign: "right",
                                  fontWeight: "600",
                                }}
                              >
                                ₹ {formatAmount(order.grossTotal)}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  textAlign: "right",
                                  color:
                                    Number(order.dueAmount) > 0
                                      ? "#dc3545"
                                      : "#1e7e34",
                                }}
                              >
                                ₹ {formatAmount(order.dueAmount)}
                              </td>
                            </tr>
                          ))}
                          {!(statsByMobile[customerMobileOf(customer)]?.orders || [])
                            .length && (
                            <tr>
                              <td
                                colSpan={6}
                                style={{
                                  textAlign: "center",
                                  color: "#6c757d",
                                  padding: "1.2rem",
                                }}
                              >
                                No orders found for this customer
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card>
                </Col>
              )}
            </React.Fragment>
          ))}
        </Row>
      )}

      {/* Per-customer product prices modal */}
      <Modal
        show={Boolean(priceCustomer)}
        onHide={handleClosePrices}
        size="lg"
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>
            <strong>Product Prices - {priceCustomer?.customerName}</strong>
            <br />
            <small
              className="text-muted"
              style={{ fontSize: "0.75rem", fontWeight: "normal" }}
            >
              Edit a price to set a customised selling price for this customer
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-2">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search product..."
              value={priceSearch}
              onChange={(e) => setPriceSearch(e.target.value)}
            />
          </InputGroup>
          {foodItemsLoading ? (
            <Loader />
          ) : (
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    {["Product", "MRP ₹", "Default ₹", "Selling Price ₹"].map(
                      (heading) => (
                        <th
                          key={heading}
                          style={{
                            textAlign: "left",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            backgroundColor: "#f4f9fd",
                            // Bootstrap paints cell backgrounds with an inset
                            // shadow layer; repeat the color there so the
                            // header is solid while rows scroll beneath it
                            boxShadow: "inset 0 0 0 9999px #f4f9fd",
                            color: "#1d5f91",
                            fontSize: "0.78rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            borderBottom: "2px solid #e3ecf3",
                            padding: "0.6rem 0.75rem",
                            whiteSpace: "nowrap",
                            ...(heading === "Selling Price ₹"
                              ? { width: "9rem" }
                              : {}),
                          }}
                        >
                          {heading}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(foodItems || [])
                    .filter(
                      (foodItem) =>
                        !priceSearch.trim() ||
                        foodItem.itemName
                          ?.toLowerCase()
                          .includes(priceSearch.trim().toLowerCase())
                    )
                    .map((foodItem) => {
                      const id = String(foodItem._id);
                      const defaultPrice = defaultSellingPrice(foodItem);
                      const mrp =
                        Number(foodItem?.availableSizes?.[0]?.itemPrice) || 0;
                      const draft = priceDrafts[id] ?? "";
                      const isCustomised =
                        String(draft).trim() !== "" &&
                        Math.abs(Number(draft) - defaultPrice) > 0.001;
                      return (
                        <tr key={id}>
                          <td style={{ textAlign: "left" }}>
                            {foodItem.itemName}{" "}
                            {isCustomised && (
                              <Badge
                                bg="success"
                                pill
                                style={{ fontSize: "0.65rem" }}
                              >
                                Customised Price
                              </Badge>
                            )}
                          </td>
                          <td style={{ textAlign: "left", color: "#6c757d" }}>
                            {formatAmount(mrp)}
                          </td>
                          <td style={{ textAlign: "left", color: "#6c757d" }}>
                            {formatAmount(defaultPrice)}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              min="0"
                              value={draft}
                              onChange={(e) =>
                                setPriceDrafts((prev) => ({
                                  ...prev,
                                  [id]: e.target.value,
                                }))
                              }
                              style={{
                                textAlign: "left",
                                fontWeight: isCustomised ? "700" : "normal",
                                borderColor: isCustomised
                                  ? "#198754"
                                  : undefined,
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  {!foodItemsLoading && !(foodItems || []).length && (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: "center",
                          color: "#6c757d",
                          padding: "1.2rem",
                        }}
                      >
                        No products found for this outlet
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div
            className="d-flex gap-2"
            style={{ width: "100%", maxWidth: "420px", margin: "0 auto" }}
          >
            <Button
              className={`${userInfo?.themeColor}ThemeHeaderTop`}
              style={{ flex: 1, padding: "0.6rem" }}
              onClick={handleSubmitPrices}
              disabled={updating || foodItemsLoading}
            >
              {updating ? "Submitting..." : "Submit"}
            </Button>
            <Button
              variant="secondary"
              style={{ flex: 1, padding: "0.6rem" }}
              onClick={handleClosePrices}
            >
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        fullscreen="sm-down"
        scrollable
        centered
        dialogClassName="customerModal"
        aria-labelledby="customer-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="customer-modal-title">
            <strong>
              {isEditMode ? "Edit Customer Details" : "Add New Customer"}
            </strong>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="my-2" controlId="customerMobile">
                  <Form.Label style={{ color: "black" }}>
                    Primary Mobile Number
                    <span style={{ color: "red" }}> *</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter 10 digit mobile number"
                    value={formData.customerMobile}
                    disabled={isEditMode}
                    onChange={(e) =>
                      handleFieldChange("customerMobile", e.target.value)
                    }
                  />
                  {isEditMode && (
                    <small className="text-muted">
                      Primary mobile number cannot be changed
                    </small>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="my-2" controlId="customerName">
                  <Form.Label style={{ color: "black" }}>
                    Customer Name<span style={{ color: "red" }}> *</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter customer name"
                    value={formData.customerName}
                    onChange={(e) =>
                      handleFieldChange("customerName", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="my-2" controlId="customerGst">
                  <Form.Label style={{ color: "black" }}>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter GST number"
                    value={formData.customerGst}
                    onChange={(e) =>
                      handleFieldChange("customerGst", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group
                  className="my-2"
                  controlId="visitorId"
                  style={{ display: "none" }}
                >
                  <Form.Label style={{ color: "black" }}>Visitor ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter visitor ID"
                    value={formData.visitorId}
                    onChange={(e) =>
                      handleFieldChange("visitorId", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="my-2" controlId="customerAddress">
              <Form.Label style={{ color: "black" }}>
                Address{" "}
                <small style={{ color: "green" }}>
                  * Enter full address with pincode as this will be used in
                  Invoices
                </small>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Enter address"
                value={formData.customerAddress}
                onChange={(e) =>
                  handleFieldChange("customerAddress", e.target.value)
                }
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="my-2" controlId="customerState">
                  <Form.Label style={{ color: "black" }}>State</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter state"
                    value={formData.customerState}
                    onChange={(e) => {
                      handleFieldChange("customerState", e.target.value);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="my-2" controlId="customerPincode">
                  <Form.Label style={{ color: "black" }}>Pincode</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter 6 digit pincode"
                    value={formData.customerPincode}
                    onChange={(e) =>
                      handleFieldChange("customerPincode", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="my-2" controlId="customerEmails">
                  <Form.Label style={{ color: "black" }}>Emails</Form.Label>
                  {formData.customerEmails.map((email, index) => (
                    <InputGroup className="mb-2" key={index}>
                      <Form.Control
                        type="email"
                        placeholder="Enter email"
                        value={email}
                        onChange={(e) =>
                          handleListChange(
                            "customerEmails",
                            index,
                            e.target.value
                          )
                        }
                      />
                      <Button
                        variant="outline-danger"
                        onClick={() =>
                          handleRemoveListItem("customerEmails", index)
                        }
                      >
                        <FaTrashAlt />
                      </Button>
                    </InputGroup>
                  ))}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleAddListItem("customerEmails")}
                  >
                    <FaPlus style={{ marginBottom: "2px" }} /> Add Email
                  </Button>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="my-2" controlId="secondaryMobiles">
                  <Form.Label style={{ color: "black" }}>
                    Secondary Mobiles
                  </Form.Label>
                  {formData.secondaryMobiles.map((mobile, index) => (
                    <InputGroup className="mb-2" key={index}>
                      <Form.Control
                        type="number"
                        placeholder="Enter mobile number"
                        value={mobile}
                        onChange={(e) =>
                          handleListChange(
                            "secondaryMobiles",
                            index,
                            e.target.value
                          )
                        }
                      />
                      <Button
                        variant="outline-danger"
                        onClick={() =>
                          handleRemoveListItem("secondaryMobiles", index)
                        }
                      >
                        <FaTrashAlt />
                      </Button>
                    </InputGroup>
                  ))}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleAddListItem("secondaryMobiles")}
                  >
                    <FaPlus style={{ marginBottom: "2px" }} /> Add Mobile
                  </Button>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="my-2" controlId="postPaidCustomer">
              <Form.Check
                type="switch"
                label="Postpaid Customer"
                checked={formData.postPaidCustomer}
                onChange={(e) =>
                  handleFieldChange("postPaidCustomer", e.target.checked)
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div
            className="d-flex gap-2"
            style={{ width: "100%", maxWidth: "420px", margin: "0 auto" }}
          >
            <Button
              className={`${userInfo?.themeColor}ThemeHeaderTop`}
              style={{ flex: 1, padding: "0.6rem" }}
              onClick={handleSubmit}
              disabled={creating || updating}
            >
              {creating || updating
                ? "Submitting..."
                : isEditMode
                ? "Update"
                : "Submit"}
            </Button>{" "}
            <Button
              variant="secondary"
              style={{ flex: 1, padding: "0.6rem" }}
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomersScreen;
